import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, Phone, ChevronRight, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingCTA from "../components/FloatingCTA";
import { blogPosts, getBlogPostBySlug, generateBlogContent } from "../data/blogPosts";

export const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return (
      <>
        <Header />
        <main className="section-padding bg-white">
          <div className="container-custom text-center">
            <h1 className="text-4xl font-bold mb-4">Article non trouvé</h1>
            <p className="text-zinc-600 mb-8">L'article que vous recherchez n'existe pas.</p>
            <Link to="/blog">
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black">
                Retour au blog
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const content = generateBlogContent(post);

  // Get related posts (excluding current)
  const relatedPosts = blogPosts.filter(p => p.slug !== slug).slice(0, 3);

  return (
    <>
      <SEOHead
        title={post.metaTitle}
        description={post.metaDescription}
        type="Article"
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-zinc-950 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={post.image}
              alt={post.imageAlt}
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/90 to-zinc-950/70" />
          </div>

          <div className="container-custom relative z-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-zinc-400 mb-8">
              <Link to="/" className="hover:text-yellow-400 transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/blog" className="hover:text-yellow-400 transition-colors">Blog</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-yellow-400 truncate max-w-[200px]">{post.title}</span>
            </nav>

            <div className="max-w-3xl">
              {/* Meta */}
              <div className="flex items-center gap-4 text-sm text-zinc-400 mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-yellow-400" />
                  {new Date(post.date).toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-400" />
                  {post.readTime} de lecture
                </div>
              </div>

              <h1 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6 font-heading" data-testid="blogpost-title">
                {post.title}
              </h1>
              
              <p className="text-lg text-zinc-300">
                {post.excerpt}
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <article className="lg:col-span-2">
                {/* Featured Image */}
                <div className="relative h-64 md:h-96 rounded-sm overflow-hidden mb-10">
                  <img
                    src={post.image}
                    alt={post.imageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Intro */}
                <div className="prose prose-lg max-w-none mb-10">
                  <p className="text-lg text-zinc-600 leading-relaxed">
                    {content.intro}
                  </p>
                </div>

                {/* Sections */}
                <div className="space-y-10">
                  {content.sections.map((section, index) => (
                    <div key={index} className="prose prose-lg max-w-none">
                      <h2 className="text-2xl font-bold uppercase tracking-tight text-zinc-900 mb-4 font-heading">
                        {section.h2}
                      </h2>
                      <p className="text-zinc-600 leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Conclusion */}
                <div className="mt-10 p-8 bg-zinc-50 border border-zinc-200 rounded-sm">
                  <h3 className="text-xl font-bold uppercase text-zinc-900 mb-4 font-heading">
                    En conclusion
                  </h3>
                  <p className="text-zinc-600 mb-6">
                    {content.conclusion}
                  </p>
                  <a href="tel:0668032561">
                    <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide">
                      <Phone className="w-5 h-5 mr-2" />
                      Appeler le 06 68 03 25 61
                    </Button>
                  </a>
                </div>

                {/* Back to blog */}
                <div className="mt-10 pt-10 border-t border-zinc-200">
                  <Link 
                    to="/blog"
                    className="inline-flex items-center gap-2 text-zinc-600 hover:text-yellow-500 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour au blog
                  </Link>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="space-y-8">
                {/* CTA Card */}
                <div className="bg-yellow-400 rounded-sm p-6 text-center sticky top-24">
                  <h3 className="text-xl font-bold uppercase tracking-tight text-black mb-4 font-heading">
                    Besoin d'aide ?
                  </h3>
                  <p className="text-black/80 mb-4 text-sm">
                    Notre serrurier intervient 24h/24
                  </p>
                  <a href="tel:0668032561" className="block">
                    <div className="bg-black text-white font-bold text-xl py-4 rounded-sm hover:bg-zinc-800 transition-colors">
                      06 68 03 25 61
                    </div>
                  </a>
                </div>

                {/* Related Articles */}
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-6">
                  <h3 className="text-xl font-bold uppercase tracking-tight text-zinc-900 mb-6 font-heading">
                    Articles similaires
                  </h3>
                  <div className="space-y-4">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.slug}`}
                        className="block group"
                      >
                        <div className="flex gap-4">
                          <img
                            src={relatedPost.image}
                            alt={relatedPost.imageAlt}
                            className="w-20 h-20 object-cover rounded-sm flex-shrink-0"
                          />
                          <div>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-yellow-500 transition-colors line-clamp-2">
                              {relatedPost.title}
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1">
                              {relatedPost.readTime}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </>
  );
};

export default BlogPost;
